import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Briefcase,
  Building2,
  Globe2,
  Plus,
  Search,
} from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkeletonCard } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const PORTAL_LABELS = {
  gem: "GeM",
  cppp: "CPPP / eProcure",
  private: "Private",
  foreign: "International",
  state_portal: "State Portal",
  other: "Other",
};

const STATUS_COLORS = {
  planning: "bg-amber-100 text-amber-800 border-amber-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  on_hold: "bg-slate-100 text-slate-800 border-slate-200",
  submitted: "bg-blue-100 text-blue-800 border-blue-200",
  closed: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function PortalBadge({ portal }) {
  if (!portal) return null;
  return (
    <Badge variant="outline" className="font-medium text-[11px]">
      {PORTAL_LABELS[portal] ?? portal}
    </Badge>
  );
}

function StatusPill({ status }) {
  const cls =
    STATUS_COLORS[status] ?? "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      {status.replace("_", " ")}
    </span>
  );
}

function NewProjectDialog({ open, onOpenChange, templates, projects, onCreated, onFinish }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("blank"); // clone | blank
  const [templateId, setTemplateId] = useState("");
  const [cloneFrom, setCloneFrom] = useState("");
  const [form, setForm] = useState({
    code: "",
    name: "",
    summary: "",
    client_name: "",
    client_type: "central_govt",
    client_state: "",
    tender_portal: "gem",
    currency: "INR",
    estimated_value: "",
    submission_deadline: "",
  });
  const [submitting, setSubmitting] = useState(false);
  // Two-step wizard: "form" (project details) → "sections" (configure
  // section templates for this project). The sections step ships the
  // standalone editor inline so a brand-new project doesn't go straight
  // into "use defaults" by default — users get a single chance to
  // override the workspace templates per project on first create.
  const [step, setStep] = useState("form");
  const [createdProject, setCreatedProject] = useState(null);

  const reset = () => {
    setStep("form");
    setCreatedProject(null);
    setMode("blank");
    setTemplateId(templates[0]?.id ?? "");
    setCloneFrom("");
    setForm({
      code: "",
      name: "",
      summary: "",
      client_name: "",
      client_type: "central_govt",
      client_state: "",
      tender_portal: "gem",
      currency: "INR",
      estimated_value: "",
      submission_deadline: "",
    });
  };

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templates.length]);

  const submit = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Project code and name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? Number(form.estimated_value) : null,
      };
      if (mode === "clone" && cloneFrom) payload.clone_from_project_id = cloneFrom;
      const created = await v2Api.projects.create(payload);
      toast.success(`Project ${created.code} created — now configure sections`);
      // Switch to the sections-config step instead of closing. Parent
      // refresh fires via onCreated so the project list updates in the
      // background; we keep the dialog open for the wizard's second
      // step. The user can "Skip & finish" or "Done" to close.
      setCreatedProject(created);
      setStep("sections");
      onCreated(created);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ?? err?.message ?? "Could not create project";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className={step !== "form" ? "max-w-3xl max-h-[90vh] overflow-y-auto" : "max-w-2xl"}>
        <DialogHeader>
          <DialogTitle>
            {step === "form" && "New Project (Space)"}
            {step === "sections" && `Configure sections — ${createdProject?.name || "project"}`}
          </DialogTitle>
          <DialogDescription>
            {step === "form" && "Start blank or clone an existing project."}
            {step === "sections" && "Step 2 — pick which sections this project's RFPs should be classified into. You can change these any time from the project page."}
          </DialogDescription>
        </DialogHeader>

        {step === "sections" && createdProject ? (
          <div className="space-y-3 pt-2">
            <div className="text-sm text-slate-600 space-y-2">
              <p>Project created. Set up section config to tell the classifier which sections to look for in this project's RFPs.</p>
              <Button size="sm" variant="outline" onClick={() => navigate(`/v2/projects/${createdProject.id}/section-config`)}>
                Configure sections →
              </Button>
            </div>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[
            { v: "clone", label: "Clone existing", icon: Briefcase },
            { v: "blank", label: "Blank", icon: Plus },
          ].map(({ v, label, icon: Icon }) => (
            <button
              key={v}
              type="button"
              onClick={() => setMode(v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                mode === v
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {mode === "clone" && (
          <div className="mb-3">
            <Label>Clone from project</Label>
            <Select value={cloneFrom} onValueChange={setCloneFrom}>
              <SelectTrigger>
                <SelectValue placeholder="Choose source project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Project code</Label>
            <Input
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="AXI-2026-014"
            />
          </div>
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="CPCB AQMS Phase IV — Western Ghats"
            />
          </div>
          <div className="col-span-2">
            <Label>Summary</Label>
            <Input
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder="One-line description for the team"
            />
          </div>
          <div>
            <Label>Client</Label>
            <Input
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="Central Pollution Control Board"
            />
          </div>
          <div>
            <Label>Client type</Label>
            <Select
              value={form.client_type}
              onValueChange={(v) => setForm({ ...form, client_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="central_govt">Central Govt</SelectItem>
                <SelectItem value="state_govt">State Govt</SelectItem>
                <SelectItem value="psu">PSU</SelectItem>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="foreign">Foreign</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tender portal</Label>
            <Select
              value={form.tender_portal}
              onValueChange={(v) => setForm({ ...form, tender_portal: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gem">GeM</SelectItem>
                <SelectItem value="cppp">CPPP</SelectItem>
                <SelectItem value="state_portal">State Portal</SelectItem>
                <SelectItem value="private">Private</SelectItem>
                <SelectItem value="foreign">International</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Currency</Label>
            <Select
              value={form.currency}
              onValueChange={(v) => setForm({ ...form, currency: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="AED">AED</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estimated value</Label>
            <Input
              type="number"
              value={form.estimated_value}
              onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
              placeholder="165000000"
            />
          </div>
          <div>
            <Label>Buyer state code (GST)</Label>
            <Input
              value={form.client_state}
              onChange={(e) => setForm({ ...form, client_state: e.target.value })}
              placeholder="07 (Delhi), 27 (Maharashtra)…"
            />
          </div>
          <div>
            <Label>Submission deadline</Label>
            <Input
              type="date"
              value={form.submission_deadline}
              onChange={(e) =>
                setForm({ ...form, submission_deadline: e.target.value })
              }
            />
          </div>
        </div>

        </>
        )}

        <DialogFooter>
          {step === "form" && (
            <>
              <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting ? "Creating…" : "Create & configure sections →"}
              </Button>
            </>
          )}
          {step === "sections" && (
            <>
              <Button variant="ghost" onClick={() => {
                const c = createdProject;
                reset();
                onOpenChange(false);
                if (c && onFinish) onFinish(c);
              }}>
                Skip
              </Button>
              <Button onClick={() => {
                const c = createdProject;
                reset();
                onOpenChange(false);
                if (c && onFinish) onFinish(c);
              }}>
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProjectsList() {
  const navigate = useNavigate();
  const { isAdmin, isDeptHead } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedImportance, setSelectedImportance] = useState("");
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  // Deeplink trigger: /v2/projects?openCreate=1[&returnTo=upload]
  // Lets external pages (notably /upload) send users into the full
  // 3-step wizard. returnTo controls where "Done" navigates back to.
  const wantOpenCreate = searchParams.get("openCreate");
  const returnTo = searchParams.get("returnTo");
  useEffect(() => {
    if (wantOpenCreate && !openCreate) {
      setOpenCreate(true);
      // Strip the param so a reload doesn't re-open.
      const next = new URLSearchParams(searchParams);
      next.delete("openCreate");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantOpenCreate]);

  const refresh = async () => {
    try {
      setLoading(true);
      const [p, t] = await Promise.all([v2Api.projects.list(), v2Api.templates()]);
      setProjects(p);
      setTemplates(t);
    } catch (err) {
      toast.error("Could not load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const allTags = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => (p.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [projects]);

  // Sidebar bifurcates into "Active projects" (?status=active) and
  // "Completed projects" (?status=completed). Treat any project whose
  // status enum is "closed" as completed; everything else (planning,
  // active, on_hold, submitted) is active. Defaults to "active" when
  // the param is absent so direct /v2/projects links keep working.
  const statusGroup = (searchParams.get("status") || "active").toLowerCase();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      const completed = p.status === "closed";
      if (statusGroup === "completed" && !completed) return false;
      if (statusGroup === "active" && completed) return false;
      if (selectedTag && !(p.tags || []).includes(selectedTag)) return false;
      if (selectedImportance && p.importance !== selectedImportance) return false;
      if (!q) return true;
      return (
        p.code.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.client_name ?? "").toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.includes(q.replace(/^#/, "")))
      );
    });
  }, [projects, query, selectedTag, selectedImportance, statusGroup]);

  return (
    <div className="px-8 py-6 max-w-[1400px] mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {statusGroup === "completed" ? "Completed projects" : "Active projects"}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {statusGroup === "completed"
              ? "Projects marked done. Read-only history of outcomes."
              : "Projects in planning, in progress, on hold, or submitted."}
          </p>
        </div>
        {isAdmin() && (
          <Button onClick={() => setOpenCreate(true)}>
            <Plus className="w-4 h-4 mr-2" /> New project
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            placeholder="Search by code, name, client or #tag"
          />
        </div>
        <span className="text-sm text-slate-500">
          {filtered.length} of {projects.length}
        </span>
      </div>

      {(allTags.length > 0 || selectedImportance) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4 text-xs">
          {allTags.length > 0 && (
            <>
              <span className="text-slate-500 mr-1">Tag:</span>
              <button
                type="button"
                onClick={() => setSelectedTag("")}
                className={`px-2 py-0.5 rounded-full border ${selectedTag === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              >
                All
              </button>
              {allTags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSelectedTag(t === selectedTag ? "" : t)}
                  className={`px-2 py-0.5 rounded-full border ${selectedTag === t ? "bg-blue-600 text-white border-blue-600" : "bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-400"}`}
                >
                  #{t}
                </button>
              ))}
              <span className="mx-2 h-4 w-px bg-slate-200" />
            </>
          )}
          <span className="text-slate-500 mr-1">Importance:</span>
          {["", "low", "medium", "high", "critical"].map((lvl) => (
            <button
              key={lvl || "any"}
              type="button"
              onClick={() => setSelectedImportance(lvl)}
              className={`px-2 py-0.5 rounded-full border capitalize ${selectedImportance === lvl ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
            >
              {lvl || "Any"}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center text-slate-500">
            <Briefcase className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No projects yet.</p>
            {isAdmin() ? (
              <>
                <p className="text-sm">Create a project to get started.</p>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button onClick={() => setOpenCreate(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Create first project
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400">No projects have been shared with you yet. Contact your admin to get access.</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Card
              key={p.id}
              className="hover:shadow-md transition cursor-pointer"
              onClick={() => navigate(`/v2/projects/${p.id}`)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
                      {p.code}
                    </p>
                    <CardTitle className="text-base leading-tight mt-1 truncate">
                      {p.name}
                    </CardTitle>
                  </div>
                  <StatusPill status={p.status} />
                </div>
              </CardHeader>
              <CardContent className="pt-0 text-sm space-y-2">
                {p.client_name && (
                  <div className="flex items-center gap-2 text-slate-600">
                    {p.client_type === "foreign" ? (
                      <Globe2 className="w-4 h-4 text-slate-400" />
                    ) : (
                      <Building2 className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="truncate">{p.client_name}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 items-center">
                  <PortalBadge portal={p.tender_portal} />
                  {p.currency !== "INR" && (
                    <Badge variant="outline" className="text-[11px]">
                      {p.currency}
                    </Badge>
                  )}
                  {p.estimated_value && (
                    <Badge variant="secondary" className="text-[11px] font-mono">
                      {new Intl.NumberFormat("en-IN", {
                        style: "currency",
                        currency: p.currency || "INR",
                        maximumFractionDigits: 0,
                      }).format(p.estimated_value)}
                    </Badge>
                  )}
                  {p.importance && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] capitalize border ${
                        p.importance === "critical" ? "bg-rose-50 text-rose-700 border-rose-200" :
                        p.importance === "high" ? "bg-amber-50 text-amber-800 border-amber-200" :
                        p.importance === "medium" ? "bg-blue-50 text-blue-700 border-blue-200" :
                        "bg-slate-50 text-slate-600 border-slate-200"
                      }`}
                    >
                      {p.importance}
                    </Badge>
                  )}
                </div>
                {(p.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {(p.tags || []).slice(0, 4).map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                        #{t}
                      </span>
                    ))}
                    {(p.tags || []).length > 4 && (
                      <span className="text-[10px] text-slate-400">+{(p.tags || []).length - 4}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-4 text-xs text-slate-500 pt-1">
                  <span>{p.bid_count} bid{p.bid_count === 1 ? "" : "s"}</span>
                  <span>{p.member_count} member{p.member_count === 1 ? "" : "s"}</span>
                  {p.submission_deadline && (
                    <span className="ml-auto">Due {p.submission_deadline}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <NewProjectDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        templates={templates}
        projects={projects}
        onCreated={(created) => {
          // Refresh the list in the background. Navigation moved into the
          // dialog's "Done" handler so step 2 (Configure sections) has
          // time to render before we unmount the dialog by navigating
          // away.
          refresh();
          return created;
        }}
        onFinish={(created) => {
          // returnTo=upload sends the user back to /upload with the
          // freshly-created project preselected — keeps the upload-
          // flow context intact when the wizard was deeplinked from
          // there.
          if (returnTo === "upload") {
            navigate(`/upload?project=${created.id}`);
          } else {
            navigate(`/v2/projects/${created.id}`);
          }
        }}
      />
    </div>
  );
}
